import {
  BATCH_ITEM_STATUS,
  buildBatchItemsFromSnapshot,
  getBatchResultImages,
  getBatchCheckpointState,
  normalizeBatchPrompts,
  resolveTurnResultUrls,
} from "./batchImageEditJob";

const TURN_KIND = {
  SUCCESS: "success",
  PENDING: "pending",
  ERROR: "error",
  INCOMPLETE: "incomplete",
};

const hasTurnResultUrls = (resultUrls) =>
  Boolean(resultUrls && Object.keys(resultUrls).length > 0);

const classifyBatchTurns = ({
  turns = [],
  isSuccess = true,
  isInProgress = false,
  responseImages = [],
  errorMessage = null,
}) =>
  turns.map((turn, index) => {
    const isLastTurn = index === turns.length - 1;
    const resultUrls = resolveTurnResultUrls({
      turn,
      isLastTurn,
      isSuccess,
      responseImages,
    });

    if (hasTurnResultUrls(resultUrls)) {
      return {
        turn,
        index,
        kind: TURN_KIND.SUCCESS,
        resultUrls,
      };
    }

    if (isLastTurn && isInProgress) {
      return {
        turn,
        index,
        kind: TURN_KIND.PENDING,
        resultUrls: null,
      };
    }

    if (isLastTurn && !isSuccess && errorMessage) {
      return {
        turn,
        index,
        kind: TURN_KIND.ERROR,
        resultUrls: null,
        errorMessage,
      };
    }

    return {
      turn,
      index,
      kind: TURN_KIND.INCOMPLETE,
      resultUrls: null,
    };
  });

const createItemsMap = (items = []) => {
  const itemsMap = {};

  items.forEach((item) => {
    if (!item?.selectionId || !item?.url) return;

    itemsMap[item.selectionId] = {
      selectionId: item.selectionId,
      sourceItem: item.sourceItem,
      index: item.index,
      blockBaseUrl: item.url,
      versions: {
        1: { url: item.url, status: BATCH_ITEM_STATUS.SUCCESS },
      },
    };
  });

  return itemsMap;
};

const writeVersionEntry = (itemsMap, selectionId, version, entry) => {
  if (!itemsMap[selectionId]) return;

  itemsMap[selectionId] = {
    ...itemsMap[selectionId],
    versions: {
      ...itemsMap[selectionId].versions,
      [version]: entry,
    },
  };
};

const applyCheckpointBlockBases = (itemsMap, latestResultUrls = {}) => {
  Object.keys(itemsMap).forEach((selectionId) => {
    const latestUrl = latestResultUrls[selectionId];
    if (!latestUrl) return;

    itemsMap[selectionId] = {
      ...itemsMap[selectionId],
      blockBaseUrl: latestUrl,
    };
  });
};

const projectClassifiedTurns = ({
  items = [],
  classifiedTurns = [],
  getVersionLabel,
  processingMessage,
}) => {
  const itemsMap = createItemsMap(items);
  const chatMessages = [];
  let globalVersion = 1;
  let visibleVersion = 1;

  classifiedTurns.forEach((classified) => {
    const { turn, index, kind, resultUrls, errorMessage } = classified;

    chatMessages.push({
      id: `batch-alert-user-${index}`,
      role: "user",
      content: turn.prompt,
      timestamp: new Date(),
      attachmentUrls: turn.attachmentUrls || [],
      attachmentFileNames: {},
    });

    switch (kind) {
      case TURN_KIND.SUCCESS: {
        globalVersion += 1;
        visibleVersion = globalVersion;

        Object.entries(resultUrls).forEach(([selectionId, url]) => {
          writeVersionEntry(itemsMap, selectionId, globalVersion, {
            url,
            status: BATCH_ITEM_STATUS.SUCCESS,
          });
        });

        chatMessages.push({
          id: `batch-alert-assistant-${index}`,
          role: "assistant",
          content: getVersionLabel(globalVersion),
          timestamp: new Date(),
          version: globalVersion,
        });
        break;
      }
      case TURN_KIND.PENDING: {
        visibleVersion = globalVersion + 1;

        Object.keys(itemsMap).forEach((selectionId) => {
          writeVersionEntry(itemsMap, selectionId, visibleVersion, {
            status: BATCH_ITEM_STATUS.PENDING,
          });
        });

        chatMessages.push({
          id: `batch-alert-thinking-${index}`,
          role: "assistant",
          content: processingMessage,
          timestamp: new Date(),
          isThinking: true,
        });
        break;
      }
      case TURN_KIND.ERROR: {
        chatMessages.push({
          id: `batch-alert-error-${index}`,
          role: "assistant",
          content: errorMessage,
          timestamp: new Date(),
          isError: true,
        });
        break;
      }
      case TURN_KIND.INCOMPLETE:
        break;
      default: {
        throw new Error(`Unexpected batch turn kind: ${kind}`);
      }
    }
  });

  return {
    itemsMap,
    chatMessages,
    globalVersion,
    visibleVersion,
  };
};

/**
 * Builds ModalBatchImageEdit session state from an editImagesBatch RTDB job snapshot.
 */
export const buildBatchImageEditSessionFromJob = ({
  payloadSnapshot = {},
  response,
  getVersionLabel = (version) => `Version ${version}`,
  processingMessage = "Thinking",
  isSuccess = true,
  isInProgress = false,
  errorMessage = null,
} = {}) => {
  const items = buildBatchItemsFromSnapshot(payloadSnapshot.items || []);
  const turns = normalizeBatchPrompts(payloadSnapshot.prompts || []);
  const responseImages = isSuccess ? getBatchResultImages(response) : [];
  const classifiedTurns = classifyBatchTurns({
    turns,
    isSuccess,
    isInProgress,
    responseImages,
    errorMessage,
  });

  const { itemsMap, chatMessages, globalVersion, visibleVersion } =
    projectClassifiedTurns({
      items,
      classifiedTurns,
      getVersionLabel,
      processingMessage,
    });

  const completedTurns = classifiedTurns
    .filter((classified) => classified.kind === TURN_KIND.SUCCESS)
    .map(({ turn, resultUrls }) => ({
      prompt: turn.prompt,
      attachmentUrls: turn.attachmentUrls,
      resultUrls,
    }));

  const { checkpointPromptsHistory, shouldResetCheckpoint, latestResultUrls } =
    getBatchCheckpointState({ completedTurns });

  if (shouldResetCheckpoint) {
    applyCheckpointBlockBases(itemsMap, latestResultUrls);
  }

  const promptsHistory = classifiedTurns.map(({ turn, resultUrls }) =>
    resultUrls
      ? {
          prompt: turn.prompt,
          attachmentUrls: turn.attachmentUrls,
          resultUrls,
        }
      : {
          prompt: turn.prompt,
          attachmentUrls: turn.attachmentUrls,
        }
  );

  return {
    initialItems: items,
    initialChatMessages: chatMessages,
    itemsMap,
    globalVersion,
    visibleVersion,
    checkpointPromptsHistory,
    promptsHistory,
    fromAlert: true,
    isJobInProgress: isInProgress,
  };
};
